import { useQuery } from '@tanstack/react-query'
import { api } from '../services'
import { queryKeys } from '../lib/queryClient'

/**
 * Sensor configuration data
 */
export interface SensorConfig {
  sensorId: number
  name: string
  type: string
  enabled: boolean
  [key: string]: any
}

/**
 * API response structure for getSensorConfig
 */
export interface GetSensorConfigResponse {
  configs: SensorConfig[]
  sigfoxId: string
  timestamp: number
  [key: string]: any
}

/**
 * Device configuration data
 */
export interface DeviceConfig {
  sigfoxId: string
  deviceId: number
  [key: string]: any
}

/**
 * React Query hook for fetching sensor configurations
 *
 * Cache Strategy:
 * - staleTime: 10 minutes (sensor configs rarely change)
 * - gcTime: 30 minutes
 * - Service Worker: StaleWhileRevalidate
 *
 * Sensor configurations define which sensors are active and how they're configured.
 * This data changes infrequently (only when user modifies sensor settings).
 *
 * @param sigfoxId - Device sigfoxId to fetch sensor config for
 * @param timestamp - Timestamp to fetch config at (default: now)
 * @param showAllSensors - Whether to show all sensors or just enabled ones
 * @param enabled - Whether to enable automatic fetching
 */
export function useSensorConfigs(
  sigfoxId: string,
  timestamp?: number,
  showAllSensors: boolean = false,
  enabled?: boolean
) {
  const ts = timestamp || Math.floor(Date.now() / 1000)
  const shouldFetch = enabled !== undefined ? enabled : !!sigfoxId

  return useQuery({
    queryKey: queryKeys.sensorConfigs(sigfoxId, ts, showAllSensors),
    queryFn: async () => {
      const result = await api.getSensorConfig(sigfoxId, ts, showAllSensors)
      return result as GetSensorConfigResponse
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - configs rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes
    enabled: shouldFetch,
    retry: 2,
  })
}

/**
 * React Query hook for fetching device configuration
 *
 * Device-level configuration (distinct from sensor-level config)
 *
 * @param sigfoxId - Device sigfoxId to fetch config for
 * @param formatted - Whether to return formatted data
 * @param enabled - Whether to enable automatic fetching
 */
export function useDeviceConfig(
  sigfoxId: string,
  formatted: boolean = true,
  enabled?: boolean
) {
  const shouldFetch = enabled !== undefined ? enabled : !!sigfoxId

  return useQuery({
    queryKey: queryKeys.deviceConfig(sigfoxId),
    queryFn: async () => {
      const result = await api.getDeviceConfig(sigfoxId, formatted)
      return result as DeviceConfig
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    enabled: shouldFetch,
    retry: 2,
  })
}

/**
 * React Query hook for fetching raw config messages
 *
 * Used for debugging and advanced configuration viewing
 *
 * @param sigfoxId - Device sigfoxId to fetch raw messages for
 * @param deviceConfig - Include device config messages
 * @param sensorConfig - Include sensor config messages
 * @param enabled - Whether to enable automatic fetching
 */
export function useRawConfigMessages(
  sigfoxId: string,
  deviceConfig: boolean = true,
  sensorConfig: boolean = true,
  enabled?: boolean
) {
  const shouldFetch = enabled !== undefined ? enabled : !!sigfoxId

  return useQuery({
    queryKey: queryKeys.rawConfigMessages(sigfoxId),
    queryFn: async () => {
      const result = await api.getRawConfigMessages(sigfoxId, deviceConfig, sensorConfig)
      return result
    },
    staleTime: 15 * 60 * 1000, // 15 minutes - raw messages rarely needed
    gcTime: 30 * 60 * 1000, // 30 minutes
    enabled: shouldFetch,
    retry: 1,
  })
}
